/**
 * Strips "empty" values out of a search-item before it's serialized into a backend
 * `searchParams` query (see FRONTEND.md §9/§12), so the backend isn't handed defaults
 * it would ignore. Works on any object by iterating its own enumerable properties;
 * the input is never mutated.
 *
 * EMPTY: null/undefined, blank/whitespace string, NaN or <= 0 number, empty array
 * (cleaned element-wise first), invalid Date, or a Date sentinel (year <= 1 =
 * DateTime.MinValue-style, year >= 9999 = "indefinite" max). Sentinels are detected on
 * `Date` instances only — date strings are treated like any other string.
 * KEPT: non-empty string (trimmed), number > 0, boolean (both true/false are meaningful),
 * non-empty array (cleaned element-wise), valid non-sentinel Date.
 */
export default class searchItemHelper {
  /** Returns a new object with only the non-empty properties of `searchItem` (strings trimmed, arrays cleaned). Does not mutate the input. */
  static removeEmptyValues<T extends object>(searchItem: T): Partial<T> {
    const result: Record<string, unknown> = {};
    if (searchItem === null || searchItem === undefined) {
      return result as Partial<T>;
    }

    Object.keys(searchItem).forEach((key) => {
      const value = (searchItem as Record<string, unknown>)[key];
      if (searchItemHelper.isEmpty(value)) {
        return;
      }
      result[key] = searchItemHelper.clean(value);
    });

    return result as Partial<T>;
  }

  /** True when `value` should be treated as "empty" (see class doc). */
  static isEmpty(value: unknown): boolean {
    if (value === null || value === undefined) {
      return true;
    }

    if (typeof value === 'string') {
      return value.trim().length === 0;
    }

    if (typeof value === 'number') {
      return Number.isNaN(value) || value <= 0;
    }

    if (typeof value === 'boolean') {
      // Both true and false are meaningful search values.
      return false;
    }

    if (value instanceof Date) {
      return searchItemHelper.isEmptyDate(value);
    }

    if (Array.isArray(value)) {
      // Empty if it has no meaningful (non-empty) elements.
      return value.every((item) => searchItemHelper.isEmpty(item));
    }

    return false;
  }

  /** True for an invalid Date or a min/max sentinel Date (year <= 1 or >= 9999). */
  static isEmptyDate(date: Date): boolean {
    if (Number.isNaN(date.getTime())) {
      return true; // Invalid Date
    }
    const year = date.getFullYear();
    return year <= 1 || year >= 9999;
  }

  /** Cleaned form of a (non-empty) value: strings trimmed, arrays cleaned element-wise, everything else as-is. */
  static clean(value: unknown): unknown {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (value instanceof Date) {
      return value;
    }

    if (Array.isArray(value)) {
      return value.filter((item) => !searchItemHelper.isEmpty(item)).map((item) => searchItemHelper.clean(item));
    }

    return value;
  }
}
